import api from '../utils/api';

/**
 * @typedef {Object} Enrollment
 * @property {string} _id
 * @property {string} user
 * @property {string} course
 * @property {string} subscription
 * @property {string} status - 'active', 'completed', 'expired', 'cancelled', 'suspended'
 * @property {boolean} isFree
 * @property {Object} paymentInfo
 * @property {number} paymentInfo.amountPaid
 * @property {number} paymentInfo.originalPrice
 * @property {number} paymentInfo.discount
 * @property {string} paymentInfo.paymentMethod
 * @property {string} paymentInfo.paymentStatus - 'pending', 'completed', 'failed', 'refunded', 'free'
 * @property {string} paymentInfo.transactionId
 * @property {string} paymentInfo.currency
 * @property {string} enrollmentDate
 * @property {string} completionDate
 * @property {string} lastAccessed
 * @property {Object} progress
 * @property {string[]} progress.completedLessons
 * @property {number} progress.percentage
 * @property {Object} watchTime
 * @property {number} watchTime.totalSeconds
 * @property {Object} usageMonitoring
 * @property {boolean} usageMonitoring.isSuspicious
 * @property {Object[]} usageMonitoring.alerts
 * @property {Object} certificate
 * @property {boolean} certificate.isIssued
 * @property {boolean} certificate.isEligible
 * @property {string} certificate.certificateNumber
 * @property {Object} finalAssessment
 * @property {number} finalAssessment.finalScore
 * @property {string} finalAssessment.grade
 * @property {boolean} finalAssessment.hasPassed
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} CreateEnrollmentData
 * @property {string} user
 * @property {string} course
 * @property {boolean} [isFree]
 * @property {string} [couponCode]
 * @property {string} [enrollmentExpiresAt]
 * @property {number} [deviceLimit]
 */

export const enrollmentService = {
  // ==================
  // STUDENT ROUTES
  // ==================

  /**
   * Get user's own enrollments
   * @param {string} userId 
   * @returns {Promise<{status: string, count: number, enrollments: Enrollment[]}>}
   */
  getEnrollmentsByUser: async (userId) => {
    const response = await api.get(`/api/enrollments/user/${userId}`);
    return response;
  },

  /**
   * Get enrollment by ID
   * @param {string} id 
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  getEnrollmentById: async (id) => {
    const response = await api.get(`/api/enrollments/${id}`);
    return response;
  },

  /**
   * Get user's active enrollments
   * @param {string} userId 
   * @returns {Promise<{status: string, count: number, enrollments: Enrollment[]}>}
   */
  getUserActiveEnrollments: async (userId) => {
    const response = await api.get(`/api/enrollments/user/${userId}/active`);
    return response;
  },

  /**
   * Check enrollment access
   * @param {string} userId 
   * @param {string} courseId 
   * @returns {Promise<{status: string, hasAccess: boolean}>}
   */
  checkEnrollmentAccess: async (userId, courseId) => {
    const response = await api.get(`/api/enrollments/access/${userId}/${courseId}`);
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Get enrollment count (Admin - for export preview)
   * @param {Object} params - Query parameters (status, isFree, courseId, etc.)
   * @returns {Promise<{status: string, total: number}>}
   */
  getEnrollmentCount: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/enrollments/admin/count${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Export enrollments without pagination (Admin)
   * @param {Object} params - Query parameters (status, isFree, courseId, etc.)
   * @returns {Promise<{status: string, count: number, enrollments: Enrollment[]}>}
   */
  exportEnrollments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/enrollments/admin/export${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Get all enrollments (Admin)
   * @param {Object} params - Query parameters (status, isFree, page, limit)
   * @returns {Promise<{status: string, count: number, total: number, page: number, totalPages: number, enrollments: Enrollment[]}>}
   */
  getAllEnrollments: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/api/enrollments/admin/all${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response;
  },

  /**
   * Create enrollment (Admin)
   * @param {CreateEnrollmentData} enrollmentData 
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  createEnrollment: async (enrollmentData) => {
    const response = await api.post('/api/enrollments', enrollmentData);
    return response;
  },

  /**
   * Get enrollments by course (Admin)
   * @param {string} courseId 
   * @returns {Promise<{status: string, count: number, enrollments: Enrollment[]}>}
   */
  getEnrollmentsByCourse: async (courseId) => {
    const response = await api.get(`/api/enrollments/course/${courseId}`);
    return response;
  },

  /**
   * Update enrollment (Admin)
   * @param {string} id 
   * @param {Object} data - Update data (status, enrollmentExpiresAt, etc.)
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  updateEnrollment: async (id, data) => {
    const response = await api.put(`/api/enrollments/${id}/status`, data);
    return response;
  },

  /**
   * Update enrollment progress
   * @param {string} id 
   * @param {Object} progressData 
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  updateEnrollmentProgress: async (id, progressData) => {
    const response = await api.put(`/api/enrollments/${id}/progress`, progressData);
    return response;
  },

  /**
   * Delete enrollment (Admin)
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteEnrollment: async (id) => {
    const response = await api.delete(`/api/enrollments/${id}`);
    return response;
  },

  // ==================
  // BULK OPERATIONS
  // ==================

  /**
   * Bulk delete enrollments
   * @param {string[]} enrollmentIds 
   * @returns {Promise<{status: string, message: string}>}
   */
  bulkDeleteEnrollments: async (enrollmentIds) => {
    const response = await api.post('/api/enrollments/bulk-delete', { enrollmentIds });
    return response;
  },

  /**
   * Bulk delete user enrollments
   * @param {string} userId 
   * @returns {Promise<{status: string, message: string}>}
   */
  bulkDeleteUserEnrollments: async (userId) => {
    const response = await api.delete(`/api/enrollments/user/${userId}/bulk-delete`);
    return response;
  },

  /**
   * Bulk delete course enrollments
   * @param {string} courseId 
   * @returns {Promise<{status: string, message: string}>}
   */
  bulkDeleteCourseEnrollments: async (courseId) => {
    const response = await api.delete(`/api/enrollments/course/${courseId}/bulk-delete`);
    return response;
  },

  // ==================
  // SUSPEND/REACTIVATE
  // ==================

  /**
   * Suspend enrollment
   * @param {string} id 
   * @param {string} reason 
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  suspendEnrollment: async (id, reason) => {
    const response = await api.put(`/api/enrollments/${id}/suspend`, { reason });
    return response;
  },

  /**
   * Reactivate enrollment
   * @param {string} id 
   * @returns {Promise<{status: string, enrollment: Enrollment}>}
   */
  reactivateEnrollment: async (id) => {
    const response = await api.put(`/api/enrollments/${id}/reactivate`);
    return response;
  },

  /**
   * Bulk suspend enrollments
   * @param {string[]} enrollmentIds
   * @param {string} reason
   * @returns {Promise<{status: string, message: string}>}
   */
  bulkSuspendEnrollments: async (enrollmentIds, reason) => {
    const response = await api.put('/api/enrollments/bulk-suspend', { enrollmentIds, reason });
    return response;
  },

  /**
   * Extend enrollment(s) by a preset duration or to a custom date.
   * Per-enrollment semantic: server adds the duration to max(today, currentExpiry).
   * @param {string[]} enrollmentIds - List of enrollment IDs to extend.
   * @param {'3m'|'6m'|'9m'|'1y'|'2y'|'3y'} [preset] - Preset duration key.
   * @param {string} [customDate] - ISO date string for custom expiry (mutually exclusive with preset).
   * @returns {Promise<{status: string, message: string, extended: Array<{id, courseId, courseTitle, previousExpiresAt, newExpiresAt}>}>}
   */
  extendEnrollments: async (enrollmentIds, preset, customDate) => {
    const payload = { enrollmentIds };
    if (preset) payload.preset = preset;
    if (customDate) payload.customDate = customDate;
    const response = await api.post('/api/enrollments/extend', payload);
    return response;
  },
};
